/* eslint-disable no-unused-vars */
import { createStore } from 'vuex'
import { poolData, categoryOfPool } from '../poolData.js'
import poolCustomization from '../poolCustomization.js'
import accessories from '../accessoryData.js'
import PlayCanvas from '../playcanvas'
import { getShouldPrice, getCountrySelectOpts, getReturnURL, fetchLocalizedData, tempRtsData } from '../storyblok.mjs'
import Bugsnag from '@bugsnag/js';

const theStore = createStore({
  state: {
    // object for properties that are specific to which site the customizer is being served for.
    // should be assigned an object, for prod instances. 
    // getters provide default values of properties if this is null (like for dev instances).  
    siteConfiguration: null,
    // if playcanvas has initialized and completed loading at least 1 pool
    playCanvasInitialized: false,
    /* 
      Catalog data - this is where strings live for all models, as well as 
        - available options per model
        - available choices per option
      This is in state because it gets modified by setTranslation
    */
    pools: poolData,
    // selected model. points to a model object in the poolData object
    model: poolData[0].series[0].models[0],
    // used for pool select screen and not necessarily corresponding to selected pool. defaults to first category
    selectedCategory: poolData[0], 
    // used for pool select screen and not necessarily corresponding to selected pool. defaults to EP series
    selectedSeries: poolData[0].series[2], 
    // ALL accessory data. This is in state because it gets modified by setTranslation.
    accessories,
    showPricing: false,
    // user selections for accessories
    selectedAccessories: [],
    // Accessory conflict popup
    accessoryConflict: { show: false, notice: 'message 1', question: 'message 2', cb: function () { } },
    // Accessory details popup data
    accessoryPopup: { show: false, accName: '' },
    // Option container
    optionsCollapsed: false,
    // Finishing options popup
    showFinishingOptionsPopup: false,
    // Simple text info popup
    showInfoPopup: false,
    infoPopupText: '',
    // User's product configuration (selected pool customization option choices)
    optionSelections: poolCustomization,
    // UI animations enable/disable - need to comment/uncomment code in _styles.scss too
    animations: true,
    // Can watch variable from component to see when loading stops
    isLoading: false,
    t: undefined,
    // currency variables
    langCode: 'en-us',
    currencyCode : 'USD'
  },
  getters: {
    manualCoverIsDisabled: (state) => {
      // Check if current pool has manual cover included and automatic cover is selected
      return state.model.includedAcc?.includes('manual-security-cover') && 
             state.selectedAccessories.includes('automatic-security-cover');
    },
    includedAccessories: state => {
      // get the included accessories for the currently selected pool
      if (state.model && state.model.includedAcc) {
        return state.model.includedAcc;
      } else {
        return [];
      }
    },
    // get formApiFormat from site configuration
    getFormApiFormat: state => {
      if (state.siteConfiguration === null) {
        // default if siteConfiguration is null (like for dev instances)
        return ''
      } else {
        return state.siteConfiguration.formApiFormat
      }
    },
    // get successPageSpa from site configuration
    getThankYouPageSpa: state => {
      if (state.siteConfiguration === null) {
        // default if siteConfiguration is null (like for dev instances)
        return '/'
      } else {
        return state.siteConfiguration.successPageSpa
      }
    },
    // get successPagePool from site configuration
    getThankYouPagePool: state => {
      if (state.siteConfiguration === null) {
        // default if siteConfiguration is null (like for dev instances)
        return '/'
      } else {
        return state.siteConfiguration.successPagePool
      }
    },
    // get formApiAddress from site configuration 
    getFormEndpoint: state => {
      // if dev API is set, use that
      if (process.env.VUE_APP_DEV_API) {
        console.log('using dev API: '+process.env.VUE_APP_DEV_API);
        return process.env.VUE_APP_DEV_API
      }
      if (state.siteConfiguration === null) {
        // default if siteConfiguration is null (like for dev instances)
        return null
      } else {
        return state.siteConfiguration.formApiAddress
      }
    },
    // get the domain of the site configuration. 
    getSiteConfigDomain: state => {
      if (state.siteConfiguration === null) {
        return null
      } else {
        return state.siteConfiguration.domain
      }
    },
    getSelectedAccessories: state => {
      return state.selectedAccessories;
    },
    getInfoPopupText: state => {
      return state.infoPopupText
    },
    // getter for user's selected option choices
    selections: state => { return state.optionSelections },
    // currently selected pool
    currPool: state => {
      return state.model
    },
    // translated text
    getT: (state) => {
      return state.t;
    },
    languages: state => {
      if (!state.t) return null;
      return getCountrySelectOpts(state.t);
    },
    rtsData: state => {
      if (!state.t) return tempRtsData;
      return getReturnURL(state.t);
    },
    poolData: (state) => {
      return state.pools;
    },
    categoryTagOfSelectedModel: (state) => {
      const cat = categoryOfPool(state.model.tag)
      return cat.tag;
    },
    linerPrice: (state, getters) => {
      const modelOptions = getters.currPool.options;
      const selections = state.optionSelections;
      // not all pools have a liner option
      const selectedLinerChoice = modelOptions.liner ? modelOptions.liner.choices.find(d=>d.id===selections.liner.value) : null;
      return selectedLinerChoice && selectedLinerChoice.cost ? selectedLinerChoice.cost : 0;
    },
    lenPrice: (state, getters) => {
      const selectedLengthObj = getters.currPool.options.len.choices.find(c => c.id === state.optionSelections.len.value);
      return selectedLengthObj && selectedLengthObj.cost ? selectedLengthObj.cost : 0;
    },
    widthPrice: (state, getters) => {
      const selectedWidthObj = getters.currPool.options.width.choices.find(c => c.id === state.optionSelections.width.value);
      return selectedWidthObj && selectedWidthObj.cost ? selectedWidthObj.cost : 0;
    },
    depthPrice: (state, getters) => {
      const selectedDepthObj = getters.currPool.options.depth.choices.find(c => c.id === state.optionSelections.depth.value);
      return selectedDepthObj && selectedDepthObj.cost ? selectedDepthObj.cost : 0;
    },
    // MSRP of model with selected options
    poolPrice: (state, getters) => {
      return getters.currPool.price + getters.depthPrice + getters.linerPrice;
    },
    // Price total = MSRP (model price) + total of selected accessories
    priceTotal: (state, getters) => {
      let total = getters.poolPrice;

      // Add prices for selected add-on accessories
      for (let key of state.selectedAccessories) {
        total += getters.summaryAccessoryPricing(key);
      }

      return total;
    },
    showPricing: state => {
      return state.showPricing;
    },
    translatedAccessoryFE: (state) => (name) => {
      const translationAcc = state?.t?.accessories;

      if (translationAcc && translationAcc.length) {
        const acc = translationAcc.find(obj => {
          const tag = obj?.content?.tag;
          if (!tag || typeof tag !== 'string') return false;
          return tag.toLowerCase() === name;
        });

        return acc;
      }

      return undefined;
    },
    translatedAccessoryByTag: state => {
      const translationAcc = state?.t?.accessories;

      if (translationAcc && translationAcc.length) {
        const acc = translationAcc.find(obj => {
          const tag = obj?.content?.tag;
          if (!tag || typeof tag !== 'string') return false;
          return tag.toLowerCase() === state.accessoryPopup.accName;
        });

        return acc;
      }

      return undefined;
    },
    // Check to hide pricing of accessory currently being shown
    popupHiddenPricing: (state, getters) => {
      const acc = getters?.translatedAccessoryByTag;

      return acc?.content?.hidden_price || false;
    },
    summaryAccessoryPricing: (state, getters) => (name) => {
      let acc = getters.translatedAccessoryFE(name)?.content;
      if (!acc) return 0;
      let price = acc.price;

      if (acc.hidden_price || !getters.showPricing) {
        return 0;
      }

      if (acc.special_price) {
        for (let combo of acc.special_price) {
          if (combo.model === state.model.sbuid) {
            price = combo.price;
            break;
          }
        }
      }

      try {
        price = parseFloat(price);
        price = isNaN(price) ? 0 : price;
      } catch (err) {
        price = 0;
        console.log(err);
        Bugsnag.notify(err);
      }
      return price;
    },
    // Get pricing of accessory currently being shown
    popupAccessoryPricing: (state, getters) => {
      let acc = getters.translatedAccessoryByTag?.content;
      if (!acc) return 0;

      let price = acc.price;

      if (acc.hidden_price) {
        return 0;
      }

      if (acc.special_price) {
        for (let combo of acc.special_price) {
          if (combo.model === state.model.sbuid) {
            price = combo.price;
            break;
          }
        }
      }

      try {
        price = parseFloat(price);
        price = isNaN(price) ? 0 : price;
      } catch (err) {
        price = 0;
        console.log(err);
        Bugsnag.notify(err);
      }

      return price;
    },
    // Get all available accessories for current model
    availableAccessories: state => {
      return state.model.addOnAcc;
    },
    isLoading: state => {
      return state.isLoading;
    },
    langCode: state => {
      return state.langCode;
    },
    currencyCode: state => {
      return state.currencyCode;
    },
  },
  mutations: {
    setPlayCanvasInitialized(state, value) {
      state.playCanvasInitialized = value;
    },
    setCategory(state, category) {
      // If changing to a different category, set the first series
      if (state.selectedCategory !== category) {
        state.selectedSeries = category.series[0];
      }
      state.selectedCategory = category;
    },
    setSeries(state, value) {
      state.selectedSeries = value;
    },
    setShowPricing(state, data) {
      state.showPricing = data;
    },
    setTranslation(state, sbResponseData) {
      /* 
          expects sbResponseData as prepared by fetchLocalizedData
      */
      if (!sbResponseData.poolCategory) return;

      // for each category (pool family) in the application's poolData object 
      state.pools.forEach(poolCategory => {
        const translatedCategory = sbResponseData.poolCategory[poolCategory.tag];
        poolCategory.title = translatedCategory.title;
        poolCategory.description = translatedCategory.description;

        // for each series in the category
        poolCategory.series.forEach(catalogSeries => {
          // if no tag property, don't worry about text for series (to accomodate unnamed series AKA line in the past)
          if (catalogSeries.tag) {
            const translatedSeries = sbResponseData.series[catalogSeries.tag];
            if (!translatedSeries) {
              console.warn(`No translation found for series tag: ${catalogSeries.tag}`);
            } else {
              catalogSeries.title = translatedSeries.title;
              catalogSeries.description = translatedSeries.description;
              // catalogSeries.popupTitle = translatedSeries.popout_title;
              // catalogSeries.shorthand = translatedSeries.shorthand;
            }
          }

          // for each pool model in the series
          catalogSeries.models.forEach(catalogPool => {
            const translatedPool = sbResponseData.models[catalogPool.tag];
            if (!translatedPool) {
              console.warn(`No translation found for pool tag: ${catalogPool.tag}`);
              // abort loop iteration and continue to next pool
              return;
            } 
            catalogPool.title = translatedPool.title;
            // catalogPool.description = translatedPool.description;
            // catalogPool.popupTitle = translatedPool.popup_title;
            // catalogPool.handle = translatedPool.handle;
            catalogPool.price = parseFloat(translatedPool.price);
            catalogPool.sbuid = translatedPool.sbuid;

            const catPoolOptions = catalogPool.options;

            // --- liner
            if (catPoolOptions.liner) {
              // iterate through properties of cost object
              catPoolOptions.liner.choices.forEach(catalogChoice => {
                // match to "value" field which is treated as an ID, here
                const translatedChoice = translatedPool.config[0].liner.find(o => o.string_id === catalogChoice.id);
                if (translatedChoice) {
                  catalogChoice.cost = parseFloat(translatedChoice.cost);
                }
              });
            }

            // --- width
            const tWidth = translatedPool.config[0].width[0];
            if (catPoolOptions.width && tWidth) {
              // assign translated unit of measurement (maybe metric)
              catPoolOptions.width.unit = tWidth.unit;
              // for each choice of this customizable option
              for (const choice of catPoolOptions.width.choices) {
                // find the choice in the translated data by ID
                const tChoice = tWidth.values.find(tC => tC.string_id === choice.id);
                // overwrite the display measurement with the translated measurement value (maybe metric)
                choice.measurement = tChoice.value;
                // overwrite the cost with the CMS cost value 
                choice.cost = parseFloat(tChoice.cost);
              }
            }

            // --- length
            const tLen = translatedPool.config[0].len[0];
            if (catPoolOptions.len && tLen) {
              // assign translated unit of measurement (maybe metric)
              catPoolOptions.len.unit = tLen.unit;
              // for each choice of this customizable option
              for (const choice of catPoolOptions.len.choices) {
                // find the choice in the translated data by ID
                const tChoice = tLen.values.find(tC => tC.string_id === choice.id);
                // overwrite the display measurement with the translated measurement value (maybe metric)
                choice.measurement = tChoice.value;
                // overwrite the cost with the CMS cost value 
                choice.cost = parseFloat(tChoice.cost);
              }
            }

            // --- depth
            const tDepth = translatedPool.config[0].depth[0];
            if (catPoolOptions.depth && tDepth) {
              // assign translated unit of measurement (maybe metric)
              catPoolOptions.depth.unit = tDepth.unit;
              // for each choice of this customizable option
              for (const choice of catPoolOptions.depth.choices) {
                // find the choice in the translated data by ID
                const tChoice = tDepth.values.find(tC => tC.string_id === choice.id);
                // overwrite the display measurement with the translated measurement value (maybe metric)
                choice.measurement = tChoice.value;
                // overwrite the cost with the CMS cost value 
                choice.cost = parseFloat(tChoice.cost);
              }
            }
          });
        });

        // --- translate accessories
        for (let key in state.accessories) {
          const localAccessory = sbResponseData.accessories.find(o => o.content.tag === key);

          if (localAccessory) {
            const accessory = state.accessories[key];
            accessory.title = localAccessory.content.title;
            accessory.description = localAccessory.content.description;
            accessory.price = parseFloat(localAccessory.content.price);
          }
        }
      });

      // persist assembled translation data
      // translations not set in the pool or accessory catalog data will be taken from here
      state.t = sbResponseData;
    },
    setIsLoading(state, val) {
      state.isLoading = val;
    },
    setModel(state, model) {
      state.model = model;
    },
    // Attempt to reuse selected options from previous model, reset values that are no longer valid
    reuseOptionSelections(state) {
      /* 
          Set option defaults, leaving user selections if options are configured to carry over, 
          and if they apply to the next pool.
      */
     
      // for all options
      for (let key of Object.keys(state.optionSelections)) {
        const configurableOption = state.optionSelections[key];
        const modelOption = state.model.options[key];

        // Check if option is available for model
        if (modelOption && configurableOption.reuse) {
          // Check that the current option value is available in this model
          // This block also runs when option value is null but new model offers option
          if (!modelOption.choices.find(d=>d.id===configurableOption.value)) {
            // Current selected option choice not available - reset to model default
            // First look for default property; else use first available value
            configurableOption.value = modelOption.default ? modelOption.default : modelOption.choices[0].id;
          }
        } else {
          // If selected choice isn't possible with current model, set option to null
          configurableOption.value = null;
        }
      }
    },
    clearAccessories(state) {
      // Clear selected accessories
      state.selectedAccessories = [];
    },
    // Basic selected option update
    // data = { name: option name, value: new selected option choice }
    updateOptionSelections(state, data) {
      state.optionSelections[data.name].value = data.value;
    },
    setLength(state, len) {
      // Update length
      state.optionSelections.len.value = len;
    },
    setWidth(state, width) {
      // Update width
      state.optionSelections.width.value = width;
    },
    setDepth(state, depth) {
      // Update depth
      state.optionSelections.depth.value = depth;
    },
    setCollapsed(state, collapsed) {
      state.optionsCollapsed = collapsed;
    },
    hideAccessoryPopup(state) {
      state.accessoryPopup.show = false;
    },
    showAccessoryPopup(state, payload) {
      state.accessoryPopup = {
        accName: payload.accName,
        show: true,
        isIncluded: payload.isIncluded
      };
    },
    selectAccessory(state, accName) {
      // add the accessory's string identifier to the list of selected accessories, if not there already
      if (!state.selectedAccessories.includes(accName)) {
        state.selectedAccessories.push(accName);
      }
      // Update PlayCanvas
      PlayCanvas.enableAccessory(accName, state.optionSelections);
    },
    showHiddenAccessory(state, accName) {
      PlayCanvas.enableAccessory(accName, state.optionSelections);
    },
    deselectAccessory(state, accName) {
      // find index of accName in selectedAccessories array
      const index = state.selectedAccessories.indexOf(accName);
      // if found, remove it
      if (index > -1) {
        state.selectedAccessories.splice(index, 1);
      }
      // Update PlayCanvas
      PlayCanvas.disableAccessory(accName);
    },
    accessoryConflict(state, { notice, question, cb }) {
      state.accessoryConflict.notice = notice;
      state.accessoryConflict.question = question;
      state.accessoryConflict.cb = cb;
      state.accessoryConflict.show = true;
    },
    resolveConflict(state, result) {
      // Call callback
      state.accessoryConflict.cb(result);
    },
    hideAccessoryConflict(state) {
      // Hide popup
      state.accessoryConflict.show = false;
    },
    showFinishingOptionsPopup(state) {
      state.showFinishingOptionsPopup = true;
    },
    hideFinishingOptionsPopup(state) {
      state.showFinishingOptionsPopup = false;
    },
    showInfoPopup(state, text) {
      state.infoPopupText = text;
      state.showInfoPopup = true;
    },
    hideInfoPopup(state) {
      state.showInfoPopup = false;
    },
    setLangCode(state, data) {
      state.langCode = data;
    },
    setCurrencyCode(state, data) {
      state.currencyCode = data;
    },
    setSiteConfiguration(state, data) {
      state.siteConfiguration = data;
    }
  },
  actions: {
    fetchTranslation(store) {
      const url = new URL(window.location.href);

      // use locale specified in URL parameter, if set
      if (url.searchParams && url.searchParams.has('lang')) {
        store.commit('setLangCode', url.searchParams.get('lang'))
      }

      return fetchLocalizedData(store.getters.langCode).then(data => {
        store.commit('setShowPricing', getShouldPrice(data));
        store.commit('setTranslation', data);
        store.commit('setCurrencyCode', data.static.currency_code);
      });
    },
    updateOptionSelections: ({ commit, state, getters }, payload) => {
      const doUpdate = () => {
        switch (payload.name) {
          case 'len': 
            commit('setLength', payload.value);
            PlayCanvas.resizePool(payload.value, state.optionSelections.width.value, state.optionSelections.depth.value);
            break;
          case 'width': 
            commit('setWidth', payload.value);
            PlayCanvas.resizePool(state.optionSelections.len.value, payload.value, state.optionSelections.depth.value);
            break;
          case 'depth': 
            commit('setDepth', payload.value);
            PlayCanvas.resizePool(state.optionSelections.len.value, state.optionSelections.width.value, payload.value);
            break;
          case 'liner': 
          case 'system': 
          case 'coping': 
          case 'skirting': 
          case 'shell': 
            commit('updateOptionSelections', payload); 
            PlayCanvas.setPoolTexture(payload.name, payload.value);
            break;
          case 'install':
            commit('updateOptionSelections', payload); 
            PlayCanvas.setInstall(payload.value);
            break;
          case 'location':
            commit('updateOptionSelections', payload); 
            PlayCanvas.setLocation(payload.value == 'INDOOR');
            break;
        }
        document.removeEventListener('hide-loading', doUpdate);
      }

      // if PlayCanvas is loading, wait until it's done loading
      if (getters.isLoading) {
        document.addEventListener('hide-loading', doUpdate);
      } else {
        doUpdate();
      }
    },
    setModel: ({ commit, state, getters }, newModel) => {
      // do nothing if model already loaded
      if (state.playCanvasInitialized && state.model == newModel) {
        return;
      }

      // Set model
      commit('setModel', newModel);
      // Reuse model selected option choices where possible
      commit('reuseOptionSelections');
      // Load model in 3D scene
      PlayCanvas.updateModel(state.model.tag, getters.categoryTagOfSelectedModel, state.optionSelections, state.model.tag);

      // Set to defaults
      const len = state.model.options.len.default || state.model.options.len.choices[0].id;
      const width = state.model.options.width.default || state.model.options.width.choices[0].id;
      const depth = state.model.options.depth.default || state.model.options.depth.choices[0].id;

      // Update UI. Put it on a delay so the numbers roll down then roll up
      setTimeout(() => {
        commit('setLength', len);
        commit('setWidth', width);
        commit('setDepth', depth);
      }, 300);

      // Update playcanvas dimensions after load 
      const resizePool = () => {
        PlayCanvas.resizePool(len, width, depth);

        // Prevent these event handlers from stacking up
        document.removeEventListener('hide-loading', resizePool);
      };
      commit('setIsLoading', true);
      document.addEventListener('hide-loading', resizePool);

      // Uncheck all accessories
      commit('clearAccessories');
    },
  },
})

document.addEventListener('hide-loading', () => {
  theStore.commit('setIsLoading', false);
  theStore.commit('setPlayCanvasInitialized', true);
});

document.addEventListener('show-loading', () => {
  theStore.commit('setIsLoading', true);
});

export default theStore;